/* global document */
/* global window */
/* global localStorage */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { bool, string } from 'prop-types';
import { IoMdClose } from 'react-icons/io';

import '../styles/ExitIntentPopUp.css';

const EXIT_INTENT_POPUP_KEY = 'exitIntentPopUpShown'; // Key to store in localStorage to track if the popup has been shown before.
const EXIT_INTENT_DELAY = 250;
const MIN_DIFF = -10;
const SCROLL_PERCENT_LIMIT = 5;
const ESCAPE_KEY = 'Escape';
const CLIENT_Y_OFFSET = 10;

/**
 * ExitIntentPopUp Component
 *
 * This component displays a modal popup with a discount message when the user
 * shows exit intent or scrolls a certain percentage of the page. The popup is
 * only shown to users who are not logged in.
 *
 * @component
 *
 * @param {Object} props - The properties object.
 * @param {string} props.bgImageUrl - The URL of the background image for the popup.
 * @param {string} props.discountMessage - The discount message to be displayed in the popup.
 * @param {string} props.discountCode - The discount code to be displayed in the popup.
 * @param {boolean} props.isLoggedIn - Flag indicating whether the user is logged in. The popup will not be shown to logged-in users.
 * @param {boolean} props.isMobile - Flag indicating whether the user is on a mobile device. The popup behavior changes based on this prop.
 *
 * @returns {React.ReactPortal|null} The ExitIntentPopUp component rendered as a portal, or null if it should not be shown.
 */
function ExitIntentPopUp({ bgImageUrl, discountMessage, discountCode, isLoggedIn, isMobile }) {
    const [container, setContainer] = useState(null);
    const [show, setShow] = useState(false);
    const [shown, setShown] = useState(false);

    // Effect to set the container element for the portal
    useEffect(() => {
        setContainer(typeof window !== 'undefined' ? document.getElementById('discount-popup') : null);
    }, []);

    // Effect to add event listeners for exit intent detection.
    useEffect(() => {
        const showPopup = () => {
            setTimeout(() => {
                setShow(true);
                setShown(true);
                localStorage.setItem(EXIT_INTENT_POPUP_KEY, 'true');
            }, EXIT_INTENT_DELAY);
        };
        
        const desktopHandler = (e) => {
            if (!shown && !isLoggedIn && e.clientY <= CLIENT_Y_OFFSET) {
                showPopup();
            }
        };

        let prevScrollY = null;
        const mobileHandler = () => {
            if (!shown && !isLoggedIn) {
                const scrollPercentage = Math.floor(window.scrollY / document.documentElement.scrollHeight * 100);
                if (scrollPercentage > SCROLL_PERCENT_LIMIT && prevScrollY !== null && (window.scrollY - prevScrollY) < MIN_DIFF) {
                    showPopup();
                }
                prevScrollY = window.scrollY;
            }
        };
        
        if (isMobile) {
            document.addEventListener('scroll', mobileHandler);
        } else {
            document.addEventListener('mouseleave', desktopHandler);
        }

        const handleUnmount = () => {
            if (isMobile) {
                document.removeEventListener('scroll', mobileHandler);
            } else {
                document.removeEventListener('mouseleave', desktopHandler);
            }
        };

        return () => {
            handleUnmount();
        };
    }, [shown, isLoggedIn, isMobile]);

    // Effect to add an event listener for the Escape key to close the popup.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === ESCAPE_KEY) {
                setShow(false);
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    });

    // Effect to check if the popup has been shown before.
    useEffect(() => {
        if (localStorage.getItem(EXIT_INTENT_POPUP_KEY)) {
            setShown(true);
        }
    }, []);

    const closePopUp = useCallback(() => setShow(false), []);

    if (container === null || !show) {
        return null;
    }

    return createPortal(
        <div
            className={'exit-intent-popup--container'}
        >
            <div
                className={'exit-intent-popup--content'}
                style={{
                    backgroundImage: `url(${bgImageUrl})`,
                }}
            >
                <IoMdClose 
                    className={'exit-intent-popup--close-icon'}
                    onClick={closePopUp}
                />
                <h1>FIRST ORDER SPECIAL</h1>
                <h2>{discountMessage}</h2>
                <h4>Use This Discount Code</h4>
                <div
                    className={'exit-intent-popup--discount-code-wrapper'}
                >
                    <span>{discountCode}</span>
                </div>
                <p
                    className={'exit-intent-popup--note'}
                >
                    Note: One Redemption Per Account/IP Address Only
                </p>
            </div>
        </div>,
        container
    );
}

ExitIntentPopUp.propTypes = {
    bgImageUrl: string.isRequired,
    discountMessage: string.isRequired,
    discountCode: string.isRequired,
    isLoggedIn: bool.isRequired,
    isMobile: bool.isRequired,
};

export default ExitIntentPopUp;